// External
import React, { useContext, useMemo, useRef, useState } from 'react';
import { connect } from 'react-redux';
import { Box, Flex } from 'rebass';
import { array, string, func } from 'prop-types';
import { CSVLink } from 'react-csv';
import { TextStyled, ButtonStyled } from '@nike/nike-design-system-components';

import { TableServerSideContext } from '~COMPONENTS/tableServerSide/TableServerSideContext';
import TableServerSide from '../../../components/tableServerSide/TableServerSide';

import ActionItemList from '../../common/ActionItemList';

import { updateProductContractGroup } from '../actions';
import { fetchAllLinkedEntities } from './actions';
import { showToast } from '../../../components/toast/actions';

import { getLinkedAccountsColumns } from './linkedAccountsColumns';

import useModal from '../../../hooks/useModal';

const LinkedAccounts = ({ productContractGroupId, linkedAccountIds, updateProductContractGroup, showToast }) => {
  const {
    totalResources,
    refreshTableData,
    sortQueryParams,
    filterQueryParams,
    handleClearSortAndFilters,
    tableInstance: { selectedRowIds = {}, selectedFlatRows, isAllRowsSelected }
  } = useContext(TableServerSideContext);

  const memoizedColumns = useMemo(() => getLinkedAccountsColumns(), []);

  const [isActionItemModalOpen, openActionItemModal, closeActionItemModal] = useModal();

  const csvLinkRef = useRef(null);

  const [allAccounts, setAllAccounts] = useState([]);

  const selectedAccountIds = selectedFlatRows?.map(row => row?.original?.id);

  const clickCSVLink = async () => setTimeout(() => csvLinkRef?.current?.link.click(), 1000);

  const handleFetchAllAccounts = async () => {
    const filterQueryParamsWithId = `&filter=id(${linkedAccountIds})${filterQueryParams}`;

    const fetchedAccounts = await fetchAllLinkedEntities({
      entity: 'accounts',
      totalResources,
      sortQueryParams,
      filterQueryParams: filterQueryParamsWithId
    });

    return setAllAccounts(fetchedAccounts);
  };

  const actionItems = {
    Download: {
      modal: {
        actions: [
          { actionName: 'Cancel', action: closeActionItemModal },
          {
            actionName: 'Download',
            action:
              ({ toggleIsLoading }) =>
              async () => {
                if (isAllRowsSelected) {
                  toggleIsLoading(true, 'Downloading CSV...');
                  await handleFetchAllAccounts(false);
                  toggleIsLoading(false);
                }

                clickCSVLink();
                closeActionItemModal();
              }
          }
        ],
        content: () => 'Do you want to proceed with downloading the selected account/s?',
        title: 'Confirm Download'
      }
    },
    '|': { action: () => {} },
    Delete: {
      modal: {
        actions: [
          { actionName: 'Cancel', action: closeActionItemModal },
          {
            actionName: 'Delete',
            action:
              ({ toggleIsLoading }) =>
              async () => {
                toggleIsLoading(true, 'Deleting account...');

                await updateProductContractGroup({
                  productContractGroupId,
                  data: { accounts: linkedAccountIds.filter(id => !selectedAccountIds.includes(id)) }
                });

                toggleIsLoading(false);
                refreshTableData();
                closeActionItemModal();

                showToast({ message: 'Account deleted', type: 'success' }, 5500);
              }
          }
        ],
        content: () => 'Do you want to proceed with deleting the selected account/s from this product contract group?',
        title: 'Confirm Deletion'
      }
    }
  };

  const csvLinkData = (isAllRowsSelected ? allAccounts : selectedFlatRows?.map(row => row.original))?.map(
    ({ number, name }) => ({ 'Account Number': number, 'Account Name': name })
  );

  return (
    <Box pt='15px'>
      <Flex alignItems='center'>
        <TextStyled appearance='body2' style={{ marginLeft: '5px', marginRight: '10px' }}>
          {`${totalResources}`} results
        </TextStyled>

        <ButtonStyled
          appearance='text'
          background='light'
          ariaLabel='Clear All Filters'
          type='button'
          onClick={handleClearSortAndFilters}
          style={{ marginRight: '10px' }}
        >
          Clear All Filters
        </ButtonStyled>

        <ActionItemList
          selected={selectedRowIds}
          actionItems={actionItems}
          isModalOpen={isActionItemModalOpen}
          openModal={openActionItemModal}
          closeModal={closeActionItemModal}
        />

        <CSVLink data={csvLinkData || []} filename='LinkedAccounts.csv' ref={csvLinkRef} target='_blank' />
      </Flex>
      <TableServerSide
        columns={memoizedColumns}
        url={`v1/accounts?filter=contractGroupId(${productContractGroupId})`}
        includeRowSelect
        fullWidth
        fullHeight
      />
    </Box>
  );
};

LinkedAccounts.propTypes = {
  productContractGroupId: string.isRequired,
  linkedAccountIds: array.isRequired,
  updateProductContractGroup: func.isRequired,
  showToast: func.isRequired
};

export default connect(null, { updateProductContractGroup, showToast })(LinkedAccounts);
